import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Users, Heart, Megaphone } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { countryName } from "@/lib/utils_gg";
import { shortDate } from "@/components/EventCard";
import StatusBadge from "@/components/StatusBadge";

const Row = ({ e, lang, testid }) => (
  <li><Link to={`/events/${e.slug}`} className="flex items-baseline gap-3 py-2.5 hover:text-primary transition-colors" data-testid={testid}>
    <span className="font-display text-primary text-sm w-24 shrink-0">{shortDate(e.start_date, lang)}</span>
    <span className="font-display uppercase truncate">{e.name}</span>
    <span className="text-xs text-muted-foreground ml-auto shrink-0">{e.city}</span></Link></li>
);

export default function MyEvents() {
  const { user, t, lang, checkingAuth } = useApp();
  const [ov, setOv] = useState(null);
  const [mine, setMine] = useState([]);
  useEffect(() => { if (user) { api.get("/my/overview").then(r => setOv(r.data)); api.get("/my/events").then(r => setMine(r.data.items)); } }, [user]);
  if (checkingAuth) return null;
  if (!user) return <Navigate to="/auth" />;
  if (!ov) return null;
  const nx = ov.next_event;
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="my-events-page">
      <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter border-b border-foreground pb-3">{t("myEventsTitle")}</h1>
      <section className="mt-6" data-testid="next-event-section">
        <div className="eyebrow text-primary mb-2">{t("yourNextEvent")}</div>
        {nx ? (
          <Link to={`/events/${nx.slug}`} className="block border border-foreground p-4 hover:border-primary transition-colors" data-testid="next-event-link">
            <div className="font-display text-primary text-sm">{shortDate(nx.start_date, lang)}</div>
            <div className="font-display uppercase text-2xl md:text-3xl leading-tight">{nx.name}</div>
            <div className="text-sm text-muted-foreground">{nx.city}, {countryName(nx.country, lang)}</div>
          </Link>
        ) : <p className="text-sm text-muted-foreground" data-testid="next-event-empty">{t("noNextEvent")}</p>}
      </section>
      <section className="mt-8 border-l-2 border-primary pl-4" data-testid="going-section">
        <div className="eyebrow mb-1 flex items-center gap-2"><Users className="w-4 h-4 text-primary" />{t("goingTab")} <span className="text-muted-foreground">({ov.going.length})</span></div>
        <ul className="divide-y divide-foreground/30 border-t border-foreground/30">{ov.going.map(e => <Row key={e.event_id} e={e} lang={lang} testid={`going-row-${e.slug}`} />)}</ul>
        {!ov.going.length && <p className="text-sm text-muted-foreground py-2">—</p>}
      </section>
      <section className="mt-8 border-l-2 border-foreground/40 pl-4" data-testid="saved-section">
        <div className="eyebrow mb-1 flex items-center gap-2"><Heart className="w-4 h-4 text-primary" />{t("savedTab")} <span className="text-muted-foreground">({ov.saved.length})</span></div>
        <ul className="divide-y divide-foreground/30 border-t border-foreground/30">{ov.saved.map(e => <Row key={e.event_id} e={e} lang={lang} testid={`saved-row-${e.slug}`} />)}</ul>
        {!ov.saved.length && <p className="text-sm text-muted-foreground py-2">—</p>}
      </section>
      {mine.length > 0 && (
        <section className="mt-10 border-t-2 border-foreground pt-6 bg-muted/30 -mx-4 md:-mx-6 px-4 md:px-6 pb-6" data-testid="organized-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="eyebrow flex items-center gap-2"><Megaphone className="w-4 h-4 text-primary" />{t("myOrganizedEvents")} <span className="text-muted-foreground">({mine.length})</span></div>
            <Link to="/dashboard" className="eyebrow underline" data-testid="organized-manage-link">{t("manage")} →</Link>
          </div>
          <ul className="divide-y divide-foreground/30 border-t border-foreground/30 mt-1">
            {mine.map(e => (
              <li key={e.event_id}><Link to={e.mod_status === "approved" ? `/events/${e.slug}` : `/events/${e.event_id}/edit`} className="flex items-center gap-3 py-2.5 hover:text-primary" data-testid={`organized-row-${e.event_id}`}>
                <span className="font-display text-primary text-sm w-24 shrink-0">{shortDate(e.start_date, lang)}</span>
                <span className="font-display uppercase truncate">{e.name}</span>
                <span className="ml-auto flex items-center gap-1.5 shrink-0"><StatusBadge status={e.mod_status} />{e.public_status && e.public_status !== "scheduled" && <span className="eyebrow !text-[10px]">{t(`public.${e.public_status}`)}</span>}</span></Link></li>))}
          </ul>
        </section>
      )}
    </div>
  );
}
